import React from "react";
import { Row, Col, Card, Typography, Button, Grid } from "antd";
import {
  UserOutlined,
  CheckCircleOutlined,
  SmileOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import CommonHeader from "../components/common/CommonHeader";
import CommonFooter from "../components/common/CommonFooter";
import propertyImage from "../assets/work.png";

const { Title, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const HowWeWorkPage = () => {
  const screens = useBreakpoint();

  const cards = [
    {
      icon: (
        <UserOutlined
          style={{ fontSize: screens.xs ? 24 : 28, color: "#C4A484" }}
        />
      ),
      title: "Choose Your Role",
      text: "Whether you are a buyer or seller, start by selecting your role.",
    },
    {
      icon: (
        <CheckCircleOutlined
          style={{ fontSize: screens.xs ? 24 : 28, color: "#C4A484" }}
        />
      ),
      title: "Browse Verified Listings",
      text: "Every property on our platform is verified and presented with transparent specs.",
    },
    {
      icon: (
        <SmileOutlined
          style={{ fontSize: screens.xs ? 24 : 28, color: "#C4A484" }}
        />
      ),
      title: "Engage Confidently",
      text: "Connect and negotiate directly with verified parties — no middlemen.",
    },
    {
      icon: (
        <HomeOutlined
          style={{ fontSize: screens.xs ? 24 : 28, color: "#C4A484" }}
        />
      ),
      title: "Seal the Deal",
      text: "Complete your transaction with trust and efficiency.",
    },
  ];

  return (
    <div style={{ background: "#F7F7F7", minHeight: "100vh" }}>
      <CommonHeader bgColor="#DDC7BB" />

      {/* Section 1 - How We Work */}
      <Row
        gutter={[0, 0]}
        align="middle"
        style={{
          padding: screens.xs
            ? "40px 20px"
            : screens.md
            ? "50px 40px"
            : "60px 100px 60px 80px",
          background: "#F7F7F7",
          overflow: "hidden",
        }}
      >
        {/* Left Text */}
        <Col xs={24} md={12}>
          <div
            style={{
              paddingRight: screens.md ? "40px" : "0px",
              textAlign: screens.xs ? "center" : "left",
              marginBottom: screens.xs ? "30px" : "0",
            }}
          >
            <Title
              level={screens.xs ? 3 : 2}
              style={{
                fontFamily: "Playfair Display, serif",
                fontWeight: "700",
                color: "#000",
                fontSize: screens.xs ? "28px" : "32px",
                marginBottom: screens.xs ? "15px" : "20px",
              }}
            >
              How we Work
            </Title>
            <Paragraph
              style={{
                color: "#555",
                fontSize: screens.xs ? 14 : 16,
                lineHeight: 1.8,
                maxWidth: "100%",
                margin: "0 auto",
                textAlign: screens.xs ? "justify" : "left",
              }}
            >
              At Not in Market, we simplify how people connect with property.
              Whether you're buying, selling, or representing, our process is
              built for clarity — not clutter. We create verified opportunities,
              streamline key interactions, and ensure every action feels
              transparent, confident, and effortless. Because in real estate,
              simplicity isn't a luxury — it's power.
            </Paragraph>
          </div>
        </Col>

        {/* Right Image */}
        <Col
          xs={24}
          md={12}
          style={{
            paddingRight: 0,
            position: "relative",
            right: screens.md ? "-100px" : "0",
          }}
        >
          <img
            src={propertyImage}
            alt="Property"
            style={{
              width: "100%",
              height: screens.xs ? "250px" : screens.md ? "350px" : "420px",
              objectFit: "cover",
              borderRadius: screens.xs ? "12px" : "60px 0 0 60px",
              display: "block",
            }}
          />
        </Col>
      </Row>

      {/* Section 2 - Cards + Buttons */}
      <div
        style={{
          background: "#F7F7F7",
          padding: screens.xs
            ? "40px 20px"
            : screens.md
            ? "50px 40px"
            : "60px 100px",
        }}
      >
        <Row gutter={[32, 32]} align="middle">
          {/* Left Cards */}
          <Col xs={24} md={16}>
            <Row gutter={[24, 24]} justify={screens.xs ? "center" : "start"}>
              {cards.map((card, index) => (
                <Col
                  xs={24}
                  sm={12}
                  md={12}
                  lg={12}
                  key={index}
                  style={{
                    display: "flex",
                    justifyContent: screens.xs ? "center" : "flex-start",
                  }}
                >
                  <Card
                    bordered={false}
                    style={{
                      borderRadius: 12,
                      boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                      width: "100%",
                      maxWidth: screens.xs ? "100%" : 320,
                      height: "100%",
                      transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    }}
                    hoverable
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        marginBottom: 10,
                        flexDirection: screens.xs ? "column" : "row",
                        textAlign: screens.xs ? "center" : "left",
                      }}
                    >
                      <div
                        style={{
                          width: screens.xs ? 45 : 50,
                          height: screens.xs ? 45 : 50,
                          borderRadius: "50%",
                          background: "#F0E6DE",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          marginBottom: screens.xs ? "8px" : "0",
                        }}
                      >
                        {card.icon}
                      </div>
                      <Title
                        level={5}
                        style={{
                          color: "#000",
                          margin: 0,
                          fontSize: screens.xs ? "14px" : "16px",
                        }}
                      >
                        {card.title}
                      </Title>
                    </div>

                    <Paragraph
                      style={{
                        color: "#555",
                        fontSize: screens.xs ? 13 : 14,
                        margin: 0,
                        lineHeight: 1.6,
                        textAlign: screens.xs ? "center" : "left",
                      }}
                    >
                      {card.text}
                    </Paragraph>
                  </Card>
                </Col>
              ))}
            </Row>
          </Col>

          {/* Right Buttons */}
          <Col xs={24} md={8}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "20px",
                alignItems: screens.xs ? "stretch" : "flex-start",
                padding: screens.xs ? "0 20px" : "0",
              }}
            >
              <Button
                type="default"
                style={{
                  border: "1px solid #000",
                  color: "#000",
                  background: "#fff",
                  height: screens.xs ? 40 : 45,
                  fontWeight: 600,
                  fontSize: screens.xs ? 14 : 16,
                  width: screens.xs ? "100%" : "80%",
                  minWidth: screens.xs ? "auto" : "200px",
                }}
                onClick={() => (window.location.href = "/policy")}
              >
                List Your Property
              </Button>
              <Button
                style={{
                  background: "#DDC7BB",
                  color: "#000",
                  border: "none",
                  height: screens.xs ? 40 : 45,
                  fontWeight: 600,
                  fontSize: screens.xs ? 14 : 16,
                  width: screens.xs ? "100%" : "80%",
                  minWidth: screens.xs ? "auto" : "200px",
                }}
                onClick={() => (window.location.href = "/listed-property")}
              >
                View Listings
              </Button>
            </div>
          </Col>
        </Row>
      </div>

      <CommonFooter />
    </div>
  );
};

export default HowWeWorkPage;
